'use client'

import { useSyncExternalStore } from 'react'
import { ToggleSwitch } from '@components/switches/ToggleSwitch/ToggleSwitch'
import { readConsent, writeConsent, isGpcEnabled, CONSENT_CHANGE_EVENT } from '@lib/consent/client'
import './ConsentSettings.css'

export interface ConsentSettingsLabels {
  essential: string
  essentialDesc: string
  alwaysOn: string
  analytics: string
  analyticsDesc: string
  gpcNotice: string
  lockedOff: string
}

function subscribeToConsent(onChange: () => void) {
  window.addEventListener(CONSENT_CHANGE_EVENT, onChange)
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onChange)
}

const subscribeNever = () => () => {}

/**
 * Cookie-preference toggles: essential (locked on) + analytics.
 * Changes apply immediately (cookie + PostHog + gated scripts).
 * Rendered in the consent banner's customize view, the account page,
 * and the cookie policy page.
 */
export default function ConsentSettings({ labels }: { labels: ConsentSettingsLabels }) {
  const analytics = useSyncExternalStore(
    subscribeToConsent,
    () => readConsent()?.analytics === true,
    () => false,
  )
  const gpc = useSyncExternalStore(subscribeNever, isGpcEnabled, () => false)

  return (
    <div id="consent-settings">
      <div id="consent-settings-essential-row" className="consent-settings-row consent-settings-row--divided">
        <div id="consent-settings-essential-copy">
          <p id="consent-settings-essential-label" className="consent-settings-label">{labels.essential}</p>
          <p id="consent-settings-essential-desc" className="consent-settings-desc">{labels.essentialDesc}</p>
        </div>
        <span id="consent-settings-essential-always-on" className="consent-settings-status">{labels.alwaysOn}</span>
      </div>
      <div id="consent-settings-analytics-row" className="consent-settings-row">
        <div id="consent-settings-analytics-copy">
          <p id="consent-settings-analytics-label" className="consent-settings-label">{labels.analytics}</p>
          <p id="consent-settings-analytics-desc" className="consent-settings-desc">{labels.analyticsDesc}</p>
          {gpc && <p id="consent-settings-gpc-notice" className="text-[11px] text-black/50 mt-1">{labels.gpcNotice}</p>}
        </div>
        {gpc ? (
          // Global Privacy Control is a binding opt-out (CCPA/CPRA): analytics
          // cannot be re-enabled while it's asserted, so this is presented as
          // a locked state (like the Essential row) rather than a toggle that
          // looks interactive but silently refuses every click.
          <span id="consent-settings-analytics-locked-off" className="consent-settings-status">{labels.lockedOff}</span>
        ) : (
          <ToggleSwitch id="consent-settings-analytics-toggle" checked={analytics} onChange={() => writeConsent(!analytics)} ariaLabel={labels.analytics} />
        )}
      </div>
    </div>
  )
}
